import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../utils/api';
import WorkflowCaseList from './WorkflowCaseList';
import MyTasksMRT from './MyTasksMRT';
import WorkflowAnalytics from './WorkflowAnalytics';
import WorkflowFlow from './WorkflowFlow';
import { useAuth } from '../contexts/AuthContext';

interface WorkflowAnalyticsData {
  total_workflows: number;
  workflows_by_stage: Record<string, number>;
  overdue_workflows: number;
  my_workflows: number;
  my_pending_tasks: number;
}

const WorkflowDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'analytics' | 'cases' | 'tasks'>('analytics');
  const [analytics, setAnalytics] = useState<WorkflowAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRecord] = useState<any>(null);
  const [diagramStageUi, setDiagramStageUi] = useState<string>('');
  const [workflowStages] = useState([
    'Register ICSR information',
    'Review',
    'Quality Check',
    'Submission',
    'Completed'
  ]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${API_BASE}/workflow/analytics/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        console.error('Failed to fetch analytics:', response.status);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // Clear any persisted stage filter on module open; start with All Cases
    try {
      localStorage.removeItem('workflow_stage_filter');
      localStorage.removeItem('workflow_stage_filter_ui');
    } catch {}
    // Open tab based on URL hash (e.g., #tasks, #cases, #analytics)
    const applyHash = () => {
      const h = (window.location.hash || '').toLowerCase();
      if (h === '#tasks') setActiveTab('tasks');
      else if (h === '#cases') setActiveTab('cases');
      else if (h === '#analytics') setActiveTab('analytics');
    };
    applyHash();
    const onHashChange = () => applyHash();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading workflow dashboard...</div>
      </div>
    );
  }

  const handleStageClick = (stage: string) => {
    // Map UI stage label to backend machine stage
    const uiToBackend: Record<string, string> = {
      'Register ICSR information': 'draft',
      'Review': 'in_review',
      'Quality Check': 'qc',
      'Submission': 'submitted',
      'Completed': 'closed'
    };
    const machine = uiToBackend[stage] || stage;
    try {
      // Notify listeners (case list) to update without remount
      window.dispatchEvent(new CustomEvent('workflow_stage_filter', { detail: { machine, ui: stage } }));
    } catch {}
    setDiagramStageUi(stage);
    setActiveTab('cases');
  };

  const backendToUi: Record<string, string> = {
    'draft': 'Register ICSR information',
    'in_review': 'Review',
    'qc': 'Quality Check',
    'submitted': 'Submission',
    'closed': 'Completed'
  };

  const stageCountsUi = (() => {
    const counts: Record<string, number> = {};
    const byStage = (analytics?.workflows_by_stage) || {};
    Object.keys(byStage).forEach(k => {
      const ui = backendToUi[k] || k;
      counts[ui] = (counts[ui] || 0) + (byStage as any)[k];
    });
    return counts;
  })();

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Case Processing Workflow</h1>
            <p className="text-gray-600 text-sm lg:text-base">Manage adverse event cases through the complete review and approval process</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button 
              onClick={() => window.location.href = '/icsr'} 
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline">Create ICSR</span>
              <span className="sm:hidden">Create</span>
            </button>
            <button 
              onClick={fetchAnalytics} 
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            {/* Bell dropdown removed for now per feedback */}
          </div>
        </div>
      </div>

      {/* Workflow Flow Visualization - enhanced diagram */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-blue-200 mb-8 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white flex items-center">
            <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Workflow Progress
          </h2>
        </div>
      <WorkflowFlow
        stages={workflowStages}
        stageCounts={stageCountsUi}
        />
      </div>

      {/* Information Board - Enhanced */}
      <div className="bg-gradient-to-br from-slate-50 to-gray-100 border border-slate-200 rounded-xl shadow-lg p-8 mb-8">
        <div className="flex items-start space-x-6">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Case Processing Workflow</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Workflow System
                  </h4>
                  <p className="text-gray-700 text-sm">Advanced workflow management system for adverse event case processing with role-based access control, automated stage transitions, and comprehensive audit trails.</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Key Capabilities
                  </h4>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• Multi-stage case progression with validation rules</li>
                    <li>• Task assignment and SLA deadline monitoring</li>
                    <li>• Workflow transitions with approval workflows</li>
                    <li>• Bulk operations and advanced filtering</li>
                    <li>• Real-time analytics and reporting</li>
                  </ul>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Workflow Stages
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">Register ICSR Information</span>
                      <span className="text-gray-500">Initial data entry and validation</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">Review</span>
                      <span className="text-gray-500">Medical and regulatory review</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">Quality Check</span>
                      <span className="text-gray-500">Data completeness and accuracy</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">Submission</span>
                      <span className="text-gray-500">Regulatory submission preparation</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700">Completed</span>
                      <span className="text-gray-500">Final approval and archival</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <nav className="flex gap-6 px-6 py-4">
            <button
              onClick={() => setActiveTab('analytics')}
            className={`py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Analytics Dashboard
            </button>
            <button
              onClick={() => setActiveTab('cases')}
            className={`py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'cases'
                  ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Case Workflows
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
            className={`py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'tasks'
                  ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              My Tasks
            </button>
          </nav>
        </div>

      {/* Notifications card removed in favor of header bell */}

        {/* Tab Content */}
      <div>
          {activeTab === 'analytics' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <WorkflowAnalytics analytics={analytics} />
          </div>
          )}
          {activeTab === 'cases' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <WorkflowCaseList />
          </div>
          )}
          {activeTab === 'tasks' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <MyTasksMRT/>
          </div>
          )}
      </div>
    </div>
  );
};

export default WorkflowDashboard;